import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last thing between a render throw and a white screen.
 *
 * The app rendered `<App/>` bare, so any exception thrown during render took the whole
 * page down with no recovery but a reload and no indication that a reload would help.
 * The route that prompted it was the admin console formatting dates with
 * `new Date(d).toISOString()`, which throws RangeError on a malformed one; that call is
 * now total (see `fmtDate` in Admin.tsx). This stays regardless. It was never meant to
 * be the fix for one known throw - it is the floor under every render path in an app
 * that draws whatever a model and an ingest pipeline hand it.
 *
 * A white screen in front of a domain expert is worse than a wrong answer, because a
 * wrong answer can be discussed. This keeps the failure legible and recoverable, and
 * shows the message rather than hiding it - whoever is standing there is usually the
 * person who can act on it.
 */
interface Props { children: ReactNode }
interface State { error: Error | null }

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept on the console rather than sent anywhere: there is no error sink yet, and a
    // stack trace in devtools is what makes this diagnosable after the fact.
    console.error('render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ maxWidth: 640, margin: '80px auto', padding: '0 20px' }}>
        <div className="card">
          <h2 className="serif" style={{ margin: '0 0 10px', fontSize: 24 }}>
            Something broke while drawing this page
          </h2>
          <p style={{ margin: '0 0 14px', lineHeight: 1.6, fontSize: 15 }}>
            The work behind it is not lost — reloading usually clears it. If it happens
            again on the same screen, this is the message to pass on:
          </p>
          <pre style={{
            margin: '0 0 16px', padding: 12, overflowX: 'auto', fontSize: 13,
            background: 'var(--blue-soft)', border: '1px solid var(--blue-line)',
            borderRadius: 6,
          }}>{error.message}</pre>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
